import React, { useEffect, useRef, useState } from 'react';
import engagebayBaseAPI from '../api';
import { LoginDeps, MfaContext } from './LoginApp';
import Testimonials from '../Testmonials';
import loadTestMonials from '../TestMonial-loader';

interface MfaScreenProps {
    context: MfaContext;
    deps: LoginDeps;
    onSuccess: (exchangeToken: string | null, redirectBase: string | null) => void;
    onBack: () => void;
}

// Resend rate-limit constants — must match email-otp.jsp behaviour so localStorage
// state from a JSESSION session is compatible with a JWT session on the same device.
const RESEND_CACHE_KEY = 'resendClickedCount';
const MAX_RESEND_ATTEMPTS = 3;
const INITIAL_COOLDOWN_SECONDS = 60;
const COOLDOWN_INCREMENT_SECONDS = 30;
const BLOCK_DURATION_MS = 60 * 60 * 1000; // 1 hour

interface ResendMeta { attempts: number; blockUntil: number; }

function getResendMeta(): ResendMeta {
    try {
        const raw = localStorage.getItem(RESEND_CACHE_KEY);
        if (!raw) return { attempts: 0, blockUntil: 0 };
        const parsed: ResendMeta = JSON.parse(raw);
        if (!Number.isInteger(parsed.attempts) || parsed.attempts < 0 || typeof parsed.blockUntil !== 'number')
            return { attempts: 0, blockUntil: 0 };
        // Auto-reset if block has expired
        if (parsed.blockUntil && Date.now() >= parsed.blockUntil) {
            const reset = { attempts: 0, blockUntil: 0 };
            localStorage.setItem(RESEND_CACHE_KEY, JSON.stringify(reset));
            return reset;
        }
        return parsed;
    } catch {
        return { attempts: 0, blockUntil: 0 };
    }
}

function saveResendMeta(meta: ResendMeta) {
    localStorage.setItem(RESEND_CACHE_KEY, JSON.stringify(meta));
}

// Exported so Login.tsx can reset the counter when a fresh MFA session begins,
// preventing stale localStorage from showing wrong remaining-resend counts on mount.
export function clearResendMeta() {
    localStorage.removeItem(RESEND_CACHE_KEY);
}

/**
 * Masks an email address to match the prod display pattern: show first 3 chars of
 * the local part, replace the remainder with ***, preserve the domain.
 * e.g. govindarajulu@outlook.com → gov***@outlook.com
 */
function maskEmail(email: string): string {
    const at = email.indexOf('@');
    if (at <= 0) return email;
    return email.slice(0, Math.min(3, at)) + '***' + email.slice(at);
}

/**
 * Standalone OTP verification screen rendered after credential login returns 202 mfa_required.
 * Contains only what is needed for verification: logo, instruction, 6-digit inputs, submit, back link.
 * No OAuth buttons, no forgot-password link, no signup link — those belong only on the credentials screen.
 *
 * Supports EMAIL_OTP (masked email display, resend with rate-limit countdown + success toast) and
 * GOOGLE_AUTHENTICATOR (remember-device checkbox).
 * Auto-submits when the 6th digit is typed or a full 6-digit code is pasted — matches JSP behaviour.
 * Resend rate-limiting uses the same localStorage key as email-otp.jsp for cross-session compatibility.
 */
const MFA_EMAIL_KEY = 'mfa_email';

const MfaScreen: React.FC<MfaScreenProps> = ({ context, deps, onSuccess, onBack }) => {
    const { securityType, redirectBase, email } = context;
    const isEmailOtp = securityType === 'EMAIL_OTP';
    const prefs = deps.prefs;

    // On first load email comes from the login response; on page refresh deps.email is absent.
    // sessionStorage survives a refresh but clears when the tab is closed, so it's the right scope.
    const [displayEmail] = useState<string | undefined>(() => {
        if (email) {
            sessionStorage.setItem(MFA_EMAIL_KEY, email);
            return email;
        }
        return sessionStorage.getItem(MFA_EMAIL_KEY) || undefined;
    });

    const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [rememberDevice, setRememberDevice] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    // True when the MFA session has ended due to OTP expiry or session invalidity.
    const [sessionExpired, setSessionExpired] = useState(false);
    // True when the user has entered wrong OTP too many times (OTP_LOCKED).
    // Both states hide OTP inputs — only "Back to Login" is shown — but the message differs.
    const [lockedOut, setLockedOut] = useState(false);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null, null, null]);

    // Resend state
    const initialMeta = getResendMeta();
    const blocked = Date.now() < initialMeta.blockUntil;
    const [resendAttempts, setResendAttempts] = useState(blocked ? MAX_RESEND_ATTEMPTS : initialMeta.attempts);
    const [resendLoading, setResendLoading] = useState(false);
    const [countdown, setCountdown] = useState(0); // >0 means timer is running
    const [shakeError, setShakeError] = useState(false);
    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        const first = inputRefs.current[0];
        if (first) first.focus();
        // Start the initial resend cooldown so the user sees the timer straight away
        startResendCooldown(INITIAL_COOLDOWN_SECONDS);
        // Hydrate testimonials on the left panel — same as Login.tsx
        if (!prefs?.isClient) loadTestMonials();
        return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
    }, []);

    // Auto-dismiss error after 6 seconds — skip when session ended so the message stays visible.
    useEffect(() => {
        if (!errorMsg || sessionExpired || lockedOut) return;
        const t = setTimeout(() => setErrorMsg(null), 6000);
        return () => clearTimeout(t);
    }, [errorMsg, sessionExpired]);

    // Auto-dismiss success (resend confirmation) after 3 seconds
    useEffect(() => {
        if (!successMsg) return;
        const t = setTimeout(() => setSuccessMsg(null), 6000);
        return () => clearTimeout(t);
    }, [successMsg]);

    function startResendCooldown(seconds: number) {
        if (countdownRef.current) clearInterval(countdownRef.current);
        setCountdown(seconds);
        let remaining = seconds;
        countdownRef.current = setInterval(() => {
            remaining--;
            setCountdown(remaining);
            if (remaining <= 0) {
                clearInterval(countdownRef.current!);
                countdownRef.current = null;
                setCountdown(0);
            }
        }, 1000);
    }

    /**
     * Core OTP submission — called directly with the assembled OTP string so React
     * state timing is irrelevant. The JSP used $button.click() on a non-disabled button;
     * we call this function with the fresh digits value instead, bypassing the disabled
     * attribute which would still reflect stale state at the moment of the call.
     */
    async function submitOtp(otp: string) {
        if (otp.length !== 6 || !/^\d{6}$/.test(otp)) return;
        if (loading) return;
        setErrorMsg(null);
        setLoading(true);

        const payload: Record<string, unknown> = { otp };
        if (rememberDevice) payload.rememberDevice = true;

        try {
            const res = await engagebayBaseAPI.post('/rest/api/login/verify-otp', payload, {
                headers: { 'x-source': 'browser' },
            });
            if (rememberDevice) localStorage.setItem('rem_goc', 'true');
            clearResendMeta();
            sessionStorage.removeItem(MFA_EMAIL_KEY);
            const data = res.data;
            onSuccess(data?.exchange_token || null, data?.redirect_base || redirectBase);
        } catch (err: unknown) {
            const data = (err as any)?.response?.data;
            const serverMsg = data?.error ?? 'Invalid or expired code. Please try again.';
            // OTP expired or locked out — errorCode from the backend is the authoritative signal.
            // MFA_SESSION_EXPIRED: cookie gone or JWT invalid. OTP_EXPIRED: Redis TTL elapsed.
            // OTP_LOCKED: too many wrong attempts. End the session for all three.
            const errorCode: string | undefined = data?.errorCode;
            if (errorCode === 'OTP_LOCKED') {
                setLockedOut(true);
            } else if (errorCode === 'MFA_SESSION_EXPIRED' || errorCode === 'OTP_EXPIRED') {
                setSessionExpired(true);
            } else {
                // Wrong code but session still active — shake the inputs, then clear and refocus.
                setShakeError(true);
                setTimeout(() => {
                    setShakeError(false);
                    setDigits(['', '', '', '', '', '']);
                    inputRefs.current[0]?.focus();
                }, 400);
            }
            setErrorMsg(serverMsg);
            setLoading(false);
        }
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        const otp = digits.join('');
        if (otp.length !== 6 || !/^\d{6}$/.test(otp)) {
            setErrorMsg('Please enter all 6 digits of the verification code.');
            return;
        }
        await submitOtp(otp);
    }

    function handleDigitChange(idx: number, value: string) {
        const digit = value.replace(/[^0-9]/g, '').slice(-1);
        const next = [...digits];
        next[idx] = digit;
        setDigits(next);
        if (digit && idx < 5) {
            const nextInput = inputRefs.current[idx + 1];
            if (nextInput) nextInput.focus();
        }
        // Auto-submit immediately with the fresh value — matches JSP checkAndSubmit(true)
        const otp = next.join('');
        if (otp.length === 6 && /^\d{6}$/.test(otp)) {
            submitOtp(otp);
        }
    }

    function handleKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
            const prev = inputRefs.current[idx - 1];
            if (prev) prev.focus();
        }
    }

    function handlePaste(e: React.ClipboardEvent) {
        e.preventDefault();
        const paste = (e.clipboardData.getData('text') || '').replace(/\D/g, '');
        if (!paste) return;
        const next = ['', '', '', '', '', ''];
        for (let i = 0; i < Math.min(paste.length, 6); i++) next[i] = paste[i];
        setDigits(next);
        const focusIdx = Math.min(paste.length, 5);
        const target = inputRefs.current[focusIdx];
        if (target) target.focus();
        // Auto-submit if all 6 digits were pasted — matches JSP paste+checkAndSubmit(true)
        const otp = next.join('');
        if (otp.length === 6 && /^\d{6}$/.test(otp)) {
            submitOtp(otp);
        }
    }

    async function handleResend(e: React.MouseEvent) {
        e.preventDefault();
        if (resendLoading || countdown > 0 || resendAttempts >= MAX_RESEND_ATTEMPTS) return;
        setResendLoading(true);
        setErrorMsg(null);
        setSuccessMsg(null);
        try {
            await engagebayBaseAPI.post('/rest/api/login/resend-otp', {}, {
                headers: { 'x-source': 'browser' },
            });
            const newAttempts = resendAttempts + 1;
            setResendAttempts(newAttempts);
            const meta: ResendMeta = {
                attempts: newAttempts,
                blockUntil: newAttempts >= MAX_RESEND_ATTEMPTS ? Date.now() + BLOCK_DURATION_MS : 0,
            };
            saveResendMeta(meta);
            // Cooldown grows with each resend: 60s, 90s, 120s — matches email-otp.jsp
            const nextCooldown = INITIAL_COOLDOWN_SECONDS + newAttempts * COOLDOWN_INCREMENT_SECONDS;
            startResendCooldown(nextCooldown);
            setSuccessMsg('Verification code has been resent successfully.');
            // Focus first digit input after resend — matches JSP focusFirstInput()
            if (inputRefs.current[0]) inputRefs.current[0].focus();
        } catch (err: unknown) {
            const data = (err as any)?.response?.data;
            const serverMsg = data?.error ?? 'Unable to resend code. Please try again.';
            // Resend limit hit — errorCode RESEND_LIMIT_EXCEEDED is the authoritative signal.
            // Fall back to HTTP 429 in case the response body is missing (network error, proxy).
            const errorCode: string | undefined = data?.errorCode;
            if (errorCode === 'MFA_SESSION_EXPIRED' || errorCode === 'RESEND_LIMIT_EXCEEDED' || (err as any)?.response?.status === 429) {
                setSessionExpired(true);
            }
            setErrorMsg(serverMsg);
        } finally {
            setResendLoading(false);
        }
    }

    const allFilled = digits.join('').length === 6;
    const resendBlocked = resendAttempts >= MAX_RESEND_ATTEMPTS;
    const remainingResends = MAX_RESEND_ATTEMPTS - resendAttempts;
    const notifyVisible = !!(errorMsg || successMsg);

    return (
        <>
            <div className={`user-verification-noty ${successMsg ? 'success' : ''} ${(notifyVisible && !sessionExpired && !lockedOut) ? '' : 'hide'}`}>
                <div className="content">
                    {notifyVisible && !sessionExpired && !lockedOut && <span>{errorMsg || successMsg}</span>}
                </div>
            </div>

            <section className="login-main-bg">
                <div className="login-video">
                    <object
                        style={{ background: 'none' }}
                        className="login-video-autoplay"
                        id="login-img-static"
                        type="image/svg+xml"
                        data="https://d2p078bqz5urf7.cloudfront.net/images/login/login-bg-animation.html"
                    />
                </div>

                <div id="content-container" className={`container h-100 ${notifyVisible ? '' : 'mt-50'}`}>
                    <div className="row h-100">
                        {/* Left panel — testimonials, same as credentials screen */}
                        <div className="col-12 col-lg-5">
                            {!prefs?.isClient && <Testimonials />}
                        </div>

                        {/* Right panel — OTP card */}
                        <div className="col-12 col-lg-5">
                            <div className="card form-info-card login-form-right">
                                <div className="form-info-header text-center pb-4">
                                    <a
                                        href={prefs?.isClient ? '#' : 'https://www.engagebay.com/'}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="logo-brand mb-4 d-inline-block"
                                    >
                                        <img
                                            className="logoimg in-iframe-hide"
                                            src={prefs?.bigLogo}
                                            alt="Logo"
                                            height="32"
                                        />
                                    </a>
                                    <h3 className="fw-500 text-dark mb-2 pb-1">Verify your Identity</h3>

                                    {/* Email hint + expiry — always visible so context is clear even when locked/expired */}
                                    {isEmailOtp ? (
                                        <>
                                            {displayEmail ? (
                                                <p className="fs-13 mb-2" style={{ backgroundColor: '#e6ffe6', padding: '16px', borderRadius: '6px' }}>
                                                    A 6-digit code has been sent to the email address <b>{maskEmail(displayEmail)}</b>
                                                </p>
                                            ) : (
                                                <p className="text-muted mb-0">
                                                    A 6-digit code has been sent to your email address.
                                                </p>
                                            )}
                                            <p className="text-muted fs-13 mt-2 mb-0">The code expires in 10 minutes.</p>
                                            <hr className="mt-3 mb-0" />
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-muted mb-0">
                                                Open your{' '}
                                                <a
                                                    href="https://support.google.com/accounts/bin/answer.py?hl=en&answer=1066447"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Google Authenticator
                                                </a>{' '}
                                                app to view your verification code.
                                            </p>
                                            <hr className="mt-3 mb-0" />
                                        </>
                                    )}
                                </div>

                                {/* Form area: OTP inputs + submit replaced by error + Back to Login when session ends */}
                                {(sessionExpired || lockedOut) ? (
                                    <div className="text-center py-3">
                                        <p className="text-danger mb-4">
                                            {lockedOut
                                                ? 'Too many incorrect attempts. Please log in again to receive a new verification code.'
                                                : 'Your verification code has expired. Please log in again to receive a new verification code.'
                                            }
                                        </p>
                                        <button
                                            type="button"
                                            className="btn btn-primary login-btn px-4"
                                            onClick={() => { clearResendMeta(); sessionStorage.removeItem(MFA_EMAIL_KEY); onBack(); }}
                                        >
                                            &larr; Back to Login
                                        </button>
                                    </div>
                                ) : <form onSubmit={handleSubmit}>
                                    {/* Resend section — shown above OTP boxes to match prod layout */}
                                    {isEmailOtp && (
                                        <div className="mb-3 text-center" style={{ fontSize: '0.875rem' }}>
                                            <p className="mb-2 fw-500 fs-13">
                                                Haven't received the code? Please check your spam folder.
                                            </p>
                                            {resendBlocked ? (
                                                <p className="text-muted mb-0">
                                                    You've reached the resend limit.
                                                </p>
                                            ) : (
                                                <>
                                                    <p className="mb-1">
                                                        If you still don't see it, click{' '}
                                                        <a
                                                            href="#"
                                                            className="text-primary"
                                                            onClick={handleResend}
                                                            style={(countdown > 0 || resendLoading) ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
                                                        >
                                                            {resendLoading ? 'Sending...' : 'Resend'}
                                                        </a>
                                                    </p>
                                                    {countdown > 0 && (
                                                        <p className="text-muted mb-0">
                                                            Resend available in <strong>{countdown}</strong> seconds
                                                        </p>
                                                    )}
                                                    {resendAttempts > 0 && (
                                                        <p className="text-muted mb-0 small">
                                                            {remainingResends} of {MAX_RESEND_ATTEMPTS} resends remaining
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/* OTP digit inputs */}
                                    <div className="form-group my-4">
                                        <div
                                            style={{ display: 'flex', gap: '8px' }}
                                            className={shakeError ? 'otp-shake' : undefined}
                                            onPaste={handlePaste}
                                        >
                                            {digits.map((d, idx) => (
                                                <input
                                                    key={idx}
                                                    ref={(el) => { inputRefs.current[idx] = el; }}
                                                    type="text"
                                                    inputMode="numeric"
                                                    autoComplete="off"
                                                    maxLength={1}
                                                    pattern="\d*"
                                                    value={d}
                                                    onChange={(e) => handleDigitChange(idx, e.target.value)}
                                                    onKeyDown={(e) => handleKeyDown(idx, e)}
                                                    className="form-control verification-code-digit"
                                                    style={{ textAlign: 'center', fontSize: '20px' }}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    {!isEmailOtp && (
                                        <div className="form-group mt-4 mb-0">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="rem_goc"
                                                    checked={rememberDevice}
                                                    onChange={(e) => setRememberDevice(e.target.checked)}
                                                />
                                                <label className="form-check-label" htmlFor="rem_goc">
                                                    Don't ask further verification codes on this device
                                                </label>
                                            </div>
                                        </div>
                                    )}

                                    <div className="d-grid mt-3">
                                        <button
                                            className="btn btn-lg btn-primary text-uppercase login-btn login-submit"
                                            type="submit"
                                            disabled={loading || !allFilled}
                                        >
                                            {loading
                                                ? <><i className="fa fa-circle-o-notch fa-spin" /> Verifying...</>
                                                : 'Verify Code'
                                            }
                                        </button>
                                    </div>

                                </form>}

                                {/* Footer — always visible regardless of expired/locked/active state */}
                                <div className="text-center mt-3 fs-13 text-muted">
                                    <p className="mb-1">©2026 EngageBay, Inc. All Rights Reserved.</p>
                                    <a
                                        href="https://www.engagebay.com/legal/privacy-policy"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Privacy Policy <span className="open-new-tab-icon"></span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default MfaScreen;
